import axios from "axios";
import { authService } from "../services/auth.service";
import { AppClient } from "../services/client.service";
import { RefreshTokenRequest } from "../generated/api-client";
import { useToast } from "../components/ToastContext";
import { useEffect } from "react";

export const axiosInstance = axios.create({ withCredentials: true });

let authInterceptorId: null | number = null;

authService.accessToken.subscribe((token: string | null) => {
  if (authInterceptorId != null) {
    axiosInstance.interceptors.request.eject(authInterceptorId);
  }
  if (token && token === "") {
    return;
  }
  authInterceptorId = axiosInstance.interceptors.request.use(
    async (config) => {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.dir(error);

    const originalRequest = error.config;

    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !(originalRequest.url as string).includes("refreshToken") &&
      !(originalRequest.url as string).includes("refresh-token")
    ) {
      try {
        if (
          authService.refreshTokenValue === "" ||
          !authService.refreshTokenValue
        ) {
          return await Promise.reject(error);
        }

        originalRequest._retry = true;

        const t = await AppClient.getInstance().refreshToken(
          AppClient.version,
          new RefreshTokenRequest({
            userId: authService.userIdValue!,
            refreshToken: authService.refreshTokenValue,
          })
        );

        authService.accessTokenValue = t!.token!;
        authService.refreshTokenValue = t!.refreshToken!;
        authService.emailValue = t!.email!;
        authService.userIdValue = t!.userId!;

        axios.defaults.headers.common["Authorization"] = "Bearer " + t!.token!;
        return axiosInstance(originalRequest);
      } catch (e: any) {
        authService.logout();
        window.location.replace("/");
      }
    }
    return Promise.reject(error);
  }
);

export const useAxiosErrorInterceptor = () => {
  const { showToast } = useToast();

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const errorsObject = error?.response?.data?.errors;
        if (errorsObject && typeof errorsObject === "object") {
          const fieldMessages = Object.values(errorsObject).flatMap((v: any) =>
            Array.isArray(v)
              ? v.filter(Boolean)
              : typeof v === "string"
                ? [v]
                : []
          );
          if (fieldMessages.length > 0) {
            fieldMessages.forEach((msg: string) => showToast(msg, "error"));
          }
        }
        const msg =
          error?.response?.data?.Detail ||
          error?.response?.data?.detail ||
          error?.detail;
        if (msg) {
          showToast(msg, "error");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [showToast]);
};
